import { JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { createEvent, sample, Store } from 'effector';
import { createEditJackpotMutation, EditJackpotMutationParams, Jackpot } from 'shared/api';
import {
	createModalFormModel,
	greaterZeroRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type JackpotEditFormValue = {
	betRate: string;
	initialAmount: string;
	limitAmount: string;
};

export type EditJackpotModel = ModalFormModel<Jackpot, JackpotEditFormValue> & {
	editJackpotMutation: Mutation<EditJackpotMutationParams, Jackpot, JsonApiRequestError>;
	$pending: Store<boolean>;
};

export const createEditJackpotModel = (): EditJackpotModel => {
	const editJackpotMutation = createEditJackpotMutation();

	const formModel = createModalFormModel<Jackpot, JackpotEditFormValue>({
		fields: {
			betRate: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
			initialAmount: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
			limitAmount: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const { $pending } = editJackpotMutation;

	const formValidated = createEvent();

	sample({
		clock: formModel.modalButtonClicked,
		fn: (jackpot) => ({
			betRate: (jackpot.betRate * 100).toFixed(2),
			initialAmount: String(jackpot.initialAmount),
			limitAmount: String(jackpot.limitAmount),
		}),
		target: [formModel.form.setValues, formModel.form.setInitialValues],
	});

	chain(formModel.modalClosed, editJackpotMutation.reset);

	sample({
		clock: formModel.form.submitted,
		source: formModel.form.$values,
		filter: ({ initialAmount, limitAmount }) => Number(limitAmount) >= Number(initialAmount),
		target: formValidated,
	});

	sample({
		clock: formModel.form.submitted,
		source: formModel.form.$values,
		filter: ({ initialAmount, limitAmount }) => Number(limitAmount) < Number(initialAmount),
		fn: () => 'one-field-greater-than-second-field',
		target: formModel.form.fields.limitAmount.addError,
	});

	sample({
		clock: formValidated,
		source: { form: formModel.form.$values, jackpot: formModel.$modalData },
		filter: ({ jackpot }) => jackpot !== null,
		fn: ({ form, jackpot }) => ({
			id: jackpot!.id,
			betRate: Number(form.betRate) / 100,
			initialAmount: Number(form.initialAmount),
			limitAmount: Number(form.limitAmount),
		}),
		target: editJackpotMutation.start,
	});

	chain(editJackpotMutation.finished.success, formModel.switcher.disable);

	notify(editJackpotMutation.finished.success, 'edit-jackpot.success');

	return {
		...formModel,
		editJackpotMutation,
		$pending,
	};
};
